import { DateTime } from 'luxon';
import moment from 'moment';
import logger from '../../Helpers/logger';
import { i18n } from '../../localization/i18n';

const TIME_FORMAT = 'HH:mm';
const DEFAULT_DATE_FORMAT = 'dd/MM/yyyy';

// Common rule 17: a message newer than this reads as "Now", and one within the
// trailing week reads as a weekday.
const RECENT_ACTIVITY_SECONDS = 60;
const SAME_WEEK_DAYS = 6;

/**
 * Reads a timestamp whatever unit it arrives in.
 *
 * The backend mixes seconds, milliseconds and microseconds across payloads, so the
 * digit count decides the unit rather than the caller.
 *
 * @param {Date|number|string} value The timestamp to read.
 * @returns {DateTime} A luxon DateTime, invalid when the value cannot be read.
 */
export const toDateTime = value => {
    if (value instanceof Date) return DateTime.fromJSDate(value);
    if (value === null || value === undefined || value === '') return DateTime.invalid('empty');

    const isNumeric = typeof value === 'number' || /^\d+$/.test(String(value).trim());
    if (!isNumeric) {
        const text = String(value).trim().replace(/:$/, ':00');
        // The server sends "YYYY-MM-DD HH:mm:ss" in UTC with no zone marker, which
        // new Date() would read as local time and shift by the viewer's offset.
        const naive = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(text);
        return naive
            ? DateTime.fromSQL(text.replace('T', ' '), { zone: 'utc' }).toLocal()
            : DateTime.fromJSDate(new Date(text));
    }

    const digits = String(Math.trunc(Number(value))).length;
    if (digits <= 10) return DateTime.fromMillis(Number(value) * 1000);
    if (digits >= 16) return DateTime.fromMillis(Math.floor(Number(value) / 1000));
    return DateTime.fromMillis(Number(value));
};

/**
 * Timestamp of a chat-list row, per common rule 17.
 *
 * Kept apart from formatChatDate, whose Today/Yesterday wording the media gallery
 * groups its days by.
 *
 * @param {Date|number|string} value A Date, or an epoch in seconds, milliseconds or microseconds.
 * @param {object} [options] Formatting options.
 * @param {string} [options.dateFormat] Luxon format of a date older than the week.
 * @returns {string} Now, HH:mm, an abbreviated weekday, or the date.
 */
export const formatActivityTime = (value, { dateFormat = DEFAULT_DATE_FORMAT } = {}) => {
    const message = toDateTime(value);
    if (!message?.isValid) return '';
    const now = DateTime.fromMillis(Date.now());

    if (now.diff(message, 'seconds').toObject().seconds < RECENT_ACTIVITY_SECONDS) {
        return i18n.t('chat.preview.NOW');
    }
    if (message.hasSame(now, 'day')) {
        return message.toFormat(TIME_FORMAT);
    }
    // Calendar days apart, so an evening message does not read as same-week from
    // the early hours of the next one.
    if (now.startOf('day').diff(message.startOf('day'), 'days').toObject().days <= SAME_WEEK_DAYS) {
        return message.toFormat('ccc');
    }
    return message.toFormat(dateFormat);
};

/**
 * formatChatDate() method to perform convert into Today, Yesterday and Date format.
 *
 * @param {date} date
 */
export const formatChatDate = date => {
    const dateCurrent = DateTime.fromMillis(Date.now());
    const dateMessage = DateTime.fromJSDate(date);
    const datesDiff = dateCurrent.diff(dateMessage, 'days').toObject().days;
    if (!datesDiff) {
        return null; /** change ? to null */
    }
    const diffRounded = Math.round(datesDiff);

    if (diffRounded === 0) {
        return 'Today';
    } else if (diffRounded === 1) {
        return 'Yesterday';
    } else if (diffRounded > 1 && diffRounded <= 6) {
        return DateTime.fromJSDate(date).toFormat('cccc');
    } else {
        const isCurrentYear = date.getFullYear() === new Date().getFullYear();
        return DateTime.fromJSDate(date).toFormat(isCurrentYear ? 'd MMM' : 'd MMM yyyy');
    }
};

/**
 * formatChatTime() method to perform convert time into now, min ago, mins ago and date format.
 *
 * @param {date} date
 * @param {string} type
 */
export const formatChatTime = date => {
    const dateCurrent = DateTime.fromMillis(Date.now());
    const dateMessage = DateTime.fromJSDate(date);
    const datesDiff = dateCurrent.diff(dateMessage, 'minutes').toObject().minutes;

    if (!datesDiff) {
        return null; /** change ? to null */
    }

    const diffRounded = Math.round(datesDiff);

    if (diffRounded === 0) {
        return 'Now';
    } else if (diffRounded === 1) {
        return `${diffRounded} min ago`;
    } else if (diffRounded < 60) {
        return `${diffRounded} mins ago`;
    } else {
        return dateMessage.toFormat(TIME_FORMAT);
    }
};

/**
 * formatChatDateTime() method to perform the format date & time.
 *
 * @param {Date} date
 * @param {string} type
 */
export const formatChatDateTime = date => {
    const timeFormatted = formatChatTime(date);

    if (timeFormatted.match(/Now|mins? ago/i)) {
        return timeFormatted;
    }
    return formatChatDate(date);
};

/**
 * convertUTCTOLocalTimeStamp() method to perform convert UTC to local time formate.
 *
 * @param {date} date
 */
export const convertUTCTOLocalTimeStamp = date => {
    date = new Date(date.replace(/-/g, '/'));
    let newDate = new Date(date);
    newDate.setMinutes(newDate.getMinutes() - newDate.getTimezoneOffset());
    return newDate;
};

function datetoTime(secs) {
    let todayDate = new Date();
    todayDate.setSeconds(todayDate.getSeconds() - secs);
    return todayDate;
}

/**
 * Formats the presence label of the other participant (FR71 Screen 71.1).
 *
 * Buckets follow the spec: today, yesterday, the previous 7 days by abbreviated
 * weekday, then dd/mm/yyyy. Comparison is by calendar day in the local timezone,
 * so a last seen just before midnight does not read as several days old.
 *
 * @param {number} secs Seconds since the participant was last seen. 0 means online.
 * @returns {string} Localised label, or an empty string when there is nothing to show.
 */
export const getLastseen = secs => {
    if (secs === 0) {
        return i18n.t('chat.detail.ONLINE');
    }
    if (typeof secs !== 'number' || secs < 0) {
        return '';
    }

    const userDate = moment(datetoTime(secs));
    const time = userDate.format(TIME_FORMAT);
    const daysAgo = moment().startOf('day').diff(userDate.clone().startOf('day'), 'days');

    if (daysAgo <= 0) {
        return i18n.t('chat.detail.LAST_SEEN_TODAY', { time });
    }
    if (daysAgo === 1) {
        return i18n.t('chat.detail.LAST_SEEN_YESTERDAY', { time });
    }
    if (daysAgo < 7) {
        return i18n.t('chat.detail.LAST_SEEN_WEEKDAY', { day: userDate.format('ddd'), time });
    }

    return i18n.t('chat.detail.LAST_SEEN_DATE', { date: userDate.format('DD/MM/YYYY'), time });
};

export const convertUTCDateToLocalDate = date => {
    if (!date || typeof date !== 'string') return 'Invalid Date';

    let cleaned = date.trim().replace(' ', 'T');
    //fix safari -- if seconds are missing -- add 00
    if (cleaned.match(/T\d{2}:\d{2}:$/)) {
        cleaned += '00';
    }

    const parsed = moment.utc(cleaned);

    if (!parsed.isValid()) {
        logger.warn('Invalid createdAt:', date);
        return 'Invalid Date';
    }

    return parsed.local().format('YYYY-MM-DD');
};

export const convertLocalDateForStarredMessage = date => {
    if (!date || typeof date !== 'string') return 'Invalid Date';

    let cleaned = date.trim().replace(' ', 'T');
    //fix safari -- if seconds are missing -- add 00
    if (cleaned.match(/T\d{2}:\d{2}:$/)) {
        cleaned += '00';
    }

    const parsed = moment.utc(cleaned);

    if (!parsed.isValid()) {
        logger.warn('Invalid createdAt:', date);
        return 'Invalid Date';
    }

    return parsed.local().format('DD MMM YYYY');
};
